import { ClipboardImport, Copy, Scissor, Cross } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { primaryModifierCombo } from '@/lib/platform';
import { useTestBlockSelection } from './TestBlockSelectionContext';

const LEFT_SIDEBAR_EXPANDED_PX = 304;
const EDGE_GUTTER_PX = 24;

interface BlockSelectionToolbarProps {
    /** Left sidebar expanded? Used to keep the pill centered over the document. */
    isSidebarExpanded: boolean;
    rightSidebarWidth: number;
}

const Hint = ({ combo, label }: { combo: string; label?: string }) => (
    <span className="flex items-center gap-1.5">
        <kbd className="rounded-md border border-border bg-surface px-1.5 py-0.5 font-sans text-[11px] font-medium leading-none text-muted-foreground">
            {combo}
        </kbd>
        {label && <span>{label}</span>}
    </span>
);

const Dot = () => <span className="size-[3px] rounded-full bg-muted-foreground/40" />;

/**
 * Floating panel for block selection. Two modes:
 * - selection: count badge + keyboard hints (the keyboard is the interface —
 *   no action buttons, the flow stays fluid);
 * - carry (after copy/cut): the persistent "you're holding N blocks" signal,
 *   with a paste-at-end shortcut and a dismiss button.
 * Centered over the document column (between the sidebars), mounted outside the
 * preview zoom container so it never scales.
 */
export function BlockSelectionToolbar({
    isSidebarExpanded,
    rightSidebarWidth,
}: BlockSelectionToolbarProps) {
    const { enabled, selectedIds, clipboardState, pasteAt, dismissClipboard } =
        useTestBlockSelection();

    if (!enabled) return null;
    if (!clipboardState && selectedIds.size === 0) return null;

    return (
        <div
            data-block-selection-ui
            className="pointer-events-none fixed bottom-6 z-50 flex items-center justify-center"
            style={{
                left: isSidebarExpanded ? `${LEFT_SIDEBAR_EXPANDED_PX}px` : `${EDGE_GUTTER_PX}px`,
                right: rightSidebarWidth > 0 ? `${rightSidebarWidth}px` : `${EDGE_GUTTER_PX}px`,
            }}
        >
            <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-border/60 bg-card/95 py-2 pl-3 pr-2 text-sm text-foreground shadow-xl backdrop-blur-md">
                {clipboardState ? (
                    <>
                        <span className="flex items-center gap-2 pl-1">
                            {clipboardState.mode === 'cut' ? (
                                <Scissor className="size-4 text-primary" />
                            ) : (
                                <Copy className="size-4 text-primary" />
                            )}
                            <span>
                                <strong className="font-semibold">{clipboardState.count}</strong>{' '}
                                {clipboardState.count === 1 ? 'bloco' : 'blocos'}{' '}
                                {clipboardState.mode === 'cut' ? 'recortado' : 'copiado'}
                                {clipboardState.count === 1 ? '' : 's'}
                                <span className="text-muted-foreground"> — escolha onde colar</span>
                            </span>
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => pasteAt('end')}>
                            <ClipboardImport />
                            Colar no final
                        </Button>
                        <Button
                            variant="ghost"
                            size="iconXs"
                            onClick={dismissClipboard}
                            aria-label="Dispensar"
                            className="rounded-full text-muted-foreground"
                        >
                            <Cross />
                        </Button>
                    </>
                ) : (
                    <>
                        <span className="flex items-center gap-2 pl-1">
                            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold tabular-nums text-primary-foreground">
                                {selectedIds.size}
                            </span>
                            <span className="font-medium">
                                {selectedIds.size === 1 ? 'selecionado' : 'selecionados'}
                            </span>
                        </span>
                        <span className="flex items-center gap-2.5 pr-2 text-xs text-muted-foreground">
                            <Hint combo={primaryModifierCombo('C')} label="copiar" />
                            <Dot />
                            <Hint combo={primaryModifierCombo('X')} label="recortar" />
                            <Dot />
                            <Hint combo="Delete" label="excluir" />
                            <Dot />
                            <Hint combo="Esc" />
                        </span>
                    </>
                )}
            </div>
        </div>
    );
}
